/**
 * Applies the operator's colour-scheme preference to the whole app.
 *
 * This used to wrap everything in Carbon's `<Theme>`, whose real job was to
 * define the `--cds-*` custom properties that every colour token aliased onto.
 * `tokens.css` now owns those values outright, and handles light and dark with
 * `prefers-color-scheme` plus a `data-theme` override — so nothing has to be
 * mounted for the palette to exist.
 *
 * What is left is the one thing CSS alone cannot do: stamp `data-theme` when a
 * preference is chosen explicitly. Nothing chooses one yet, so this currently
 * follows the OS and stays out of the way — but the seam is here, and the
 * tokens already honour it in both directions.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';

export function ThemeRoot({ children }: { children: ReactNode }) {
  useEffect(() => {
    // `color-scheme` on :root already tells the browser how to paint form
    // controls and scrollbars per media query. This only keeps the document
    // element in step for anything that reads the attribute directly.
    const root = document.documentElement;
    if (!root.hasAttribute('data-theme')) {
      root.style.colorScheme = '';
    }
  }, []);

  return <>{children}</>;
}
